import React, {Component} from 'react';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Button from '../../../components/button';

const scopeLabel = (s) => (s.type === 'global' ? 'global' : `${s.type}:${s.id}`);

/**
 * What actually carries the "what happened" for the two commonest events (docs/access/
 * slice-4b-admin-surface-plan.md §3.5, code review). For a ROLE grant — the only kind the drawer
 * creates — `createAssignment` writes `permission_code`/`scope_type` NULL on the row itself
 * whenever there is more than one scope; the role, scopes and note live only in `detail`.
 *
 * REVOKES DO CARRY A ROLE, and an earlier version of this comment asserted they never could.
 * 4a's `revokeAssignment` writes `detail.assignmentId`, which is what that claim was reasoned
 * from — but `scripts/access/assign-role.js` writes `{role, via}`, and it is what produced every
 * revoke row on dev. Generalising from one writer to "not recoverable from this row at all" put a
 * blank cell next to data that was sitting right there. Read the role first and fall back, rather
 * than deciding per writer what a row is allowed to contain.
 *
 * @param {object} e One `ac_audit` row.
 * @return {string} The Detail cell's text.
 */
const describeDetail = (e) => {
  const detail = e.detail || {};
  if (e.event_type === 'grant') {
    const who = detail.role || e.permission_code || '';
    const scopes = detail.scopes || (e.scope_type ? [{type: e.scope_type, id: e.scope_id}] : []);
    const scopeText = scopes.map(scopeLabel).join(', ');
    const note = detail.note ? ` — ${detail.note}` : '';
    return `← ${who}${scopeText ? ` @ ${scopeText}` : ''}${note}`;
  }
  if (e.event_type === 'revoke') {
    const who = detail.role || e.permission_code || '';
    if (who) return `✕ ${who}`;
    return detail.assignmentId ? `✕ assignment #${detail.assignmentId}` : '';
  }
  if (e.event_type === 'escalation_blocked') {
    const reason = detail.reason ? ` (${detail.reason})` : '';
    return `${detail.pair || detail.message || ''}${reason}`;
  }
  return `${e.permission_code || ''}${e.scope_type ? ` @ ${e.scope_type}:${e.scope_id || ''}` : ''}`;
};

/**
 * `GET /access/audit` (docs/access/slice-4b-admin-surface-plan.md §5). `detail` is returned
 * verbatim by the server (§3.5) — grant/revoke rows carry `role`, `scopes`, `note`, `via`, and
 * `escalation_blocked` rows carry the refused (permission, scope) pair.
 */
class AuditLog extends Component {
  /** CSV of what is on screen — the fetched page, not a server export job. */
  handleExport = () => {
    const rows = [['When', 'Event', 'Actor', 'Target', 'Detail']];
    (this.props.events || []).forEach((e) => rows.push([
      e.created_at ? new Date(e.created_at).toISOString() : '',
      e.event_type, e.actor_name || e.actor_subject_id,
      e.target_name || (e.target_subject_id != null ? e.target_subject_id : ''),
      describeDetail(e),
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], {type: 'text/csv'}));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'access-audit.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  render() {
    const rows = this.props.events || [];
    const filters = this.props.filters || {};
    const onFilterChange = this.props.onFilterChange || (() => {});

    return (
      <div className="rounded-card audit-panel">
        <h3>
          Audit
          {/* S8's chips. `sensitiveOnly` is not a permission_code filter — a ROLE grant carries no
              permission_code at all, so a naive one would drop "granted data_owner" while keeping
              single-permission trivia. The server defines it over three terms; see listAudit. */}
          <span className="audit-chips">
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(filters.sensitiveOnly)}
                  onChange={(e) => onFilterChange({sensitiveOnly: e.target.checked})}
                  inputProps={{'data-testid': 'ac-audit-sensitive'}}
                />
              }
              label="Sensitive only"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(filters.since)}
                  onChange={(e) => onFilterChange({
                    since: e.target.checked ?
                      new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString() : undefined,
                  })}
                  inputProps={{'data-testid': 'ac-audit-last7'}}
                />
              }
              label="Last 7 days"
            />
            <Button label="Export" className="btn btn-default" onClick={this.handleExport}
              data-testid="ac-audit-export" />
          </span>
        </h3>
        <Table size="small" className="data-table">
          <TableHead>
            <TableRow>
              <TableCell><strong>When</strong></TableCell>
              <TableCell><strong>Event</strong></TableCell>
              <TableCell><strong>Actor</strong></TableCell>
              <TableCell><strong>Target</strong></TableCell>
              <TableCell><strong>Detail</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!rows.length && (
              <TableRow><TableCell colSpan={5}>No audit events.</TableCell></TableRow>
            )}
            {rows.map((e) => (
              <TableRow key={e.id} hover data-testid="ac-audit-row">
                <TableCell>{e.created_at ? new Date(e.created_at).toLocaleString() : '—'}</TableCell>
                <TableCell>{e.event_type}</TableCell>
                {/*
                  Names, with the id only as a fallback. `ac_subject.id` means nothing to a reader
                  of an audit trail — "actor 1, target 9" answers no question anyone asks — and it
                  is a DIFFERENT key space from `user.iduser`, so a reader who tries to interpret
                  it lands on the wrong person. Resolved server-side by a LEFT JOIN in `listAudit`,
                  so a subject deleted since the event still renders as its id rather than blank.
                */}
                <TableCell>{e.actor_name || e.actor_subject_id}</TableCell>
                <TableCell>{e.target_name || (e.target_subject_id != null ? e.target_subject_id : '—')}</TableCell>
                <TableCell data-testid="ac-audit-detail">{describeDetail(e)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }
}

export default AuditLog;
